import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Linkedin, Globe, Twitter, Facebook, Instagram, Pencil, CalendarDays, FileText, ArrowLeft } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { fileUrl } from "@/lib/api";
import { RichTextView } from "@/components/forms/RichTextEditor";
import { VideoEmbed } from "@/components/common/VideoEmbed";
import { useReferences, labelFor } from "@/hooks/useReferences";
import { ConnectButton, MessageButton, BookmarkButton, FanFollowButtons } from "@/components/social/ProfileActions";
import { ShareProfileButton } from "@/components/social/ShareProfileButton";
import { StaffMessageButton } from "@/components/social/StaffMessageButton";
import { ReportMenu } from "@/components/social/ReportMenu";
import { ConnectionsCard } from "@/components/social/Connections";
import { AskQuestionButton } from "@/components/social/AskQuestionDialog";
import { InquiryButton } from "@/components/social/InquiryDialog";
import { ChatterFeed } from "@/components/social/ChatterFeed";
import { PublicQna } from "@/components/social/PublicQna";
import { AccountTagInline } from "@/components/tags/AccountTag";
import { trackOutboundLink, trackInternalLink } from "@/lib/analytics";

const loc = (l) => [l?.city, l?.state, l?.country].filter(Boolean).join(", ");

const SOCIALS = [
  ["website", Globe, "Website"],
  ["linkedin", Linkedin, "LinkedIn"],
  ["x", Twitter, "X"],
  ["facebook", Facebook, "Facebook"],
  ["instagram", Instagram, "Instagram"],
];

export function PersonalProfileView({ profile }) {
  const { references } = useReferences();
  const links = profile.links || {};
  const socialLinks = SOCIALS.filter(([k]) => links[k]);
  const showSection = (v) => v && (Array.isArray(v) ? v.length > 0 : true);
  const firstName = profile.display_name?.split(" ")[0] || "this member";
  // Public Links: an unauthenticated visitor gets the same layout, read-only — every
  // member-only interaction is withheld rather than rendered and then refused.
  const publicMode = profile.is_public_view === true;
  const hasBackground = profile.background && profile.background !== "<p></p>";
  const hasIndustries = showSection(profile.industries);
  const hasSkills = showSection(profile.skills);
  const events = profile.contributed_events || [];
  const resources = profile.contributed_resources || [];
  const hasContributions = events.length > 0 || resources.length > 0;

  return (
    <div className="bg-background">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mt-4">
          <Link to="/people" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="profile-back-link"><ArrowLeft className="h-4 w-4" /> All people</Link>
        </div>
        {/* Cover (contained to profile width, not edge-to-edge) */}
        <div className="relative mt-3 h-40 w-full overflow-hidden bg-gradient-to-r from-teal-100 to-teal-50 shadow-[0_8px_30px_-12px_rgba(16,24,40,0.25)] sm:h-56">
          {profile.cover_url && <img src={fileUrl(profile.cover_url)} alt="cover" className="h-full w-full object-cover" />}
          {profile.is_owner && (
            <Link to="/dashboard/profile" className="absolute right-3 top-3" data-testid="profile-edit-button">
              <Button size="sm" variant="secondary" className="shadow-sm"><Pencil className="mr-1.5 h-4 w-4" /> Edit profile</Button>
            </Link>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-5 pb-16 lg:grid-cols-12">
          {/* LEFT (slim) */}
          <div className="space-y-5 lg:col-span-4">
            <Card className="relative border-0 p-6">
              <div className="absolute right-4 top-4 z-10 flex items-center gap-0.5">
                <ShareProfileButton handle={profile.handle} slug={profile.slug} name={profile.display_name}
                  isOwner={profile.is_owner === true} publicLinkEnabled={profile.public_link_enabled === true} />
                {!profile.is_owner && !publicMode && (
                  <>
                    <BookmarkButton itemType="personal" itemId={profile.id} iconOnly variant="ghost" />
                    <ReportMenu contentType="personal" contentId={profile.id} ownerUserId={profile.user_id} testId="report-menu-personal"
                      blockTargetUserId={profile.user_id} blockTargetName={profile.display_name}
                      blockSource="personal_profile"
                      onBlockChanged={() => window.location.reload()} />
                  </>
                )}
              </div>
              <Avatar className="h-36 w-36 border-4 border-card shadow-sm">
                {profile.headshot_url && <AvatarImage src={fileUrl(profile.headshot_url)} alt={profile.display_name} className="object-cover" />}
                <AvatarFallback className="bg-teal-50 text-2xl text-teal-700">{(profile.display_name || "U")[0]}</AvatarFallback>
              </Avatar>
              <h1 className="mt-4 text-xl font-[700] text-foreground break-words">{profile.display_name}</h1>
              {profile.handle && <Link to={`/@${profile.handle}`} className="text-sm font-[500] text-primary hover:underline" data-testid="profile-handle-link">@{profile.handle}</Link>}
              {profile.account_tags?.length > 0 && (
                <AccountTagInline tags={profile.account_tags} className="mt-1.5" testId="profile-account-tags" />
              )}
              {profile.pronouns && profile.pronouns.trim().toLowerCase() !== "prefer not to say" && <div className="mt-0.5 text-sm text-muted-foreground" data-testid="profile-pronouns-display">{profile.pronouns}</div>}
              {profile.headline && <p className="mt-2 text-sm text-foreground">{profile.headline}</p>}
              {loc(profile.location) && <div className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin className="h-4 w-4" /> {loc(profile.location)}</div>}
              {!profile.is_owner && !publicMode && (
                <div className="mt-4 space-y-2">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <ConnectButton targetUserId={profile.user_id} className="w-full justify-center" />
                    <FanFollowButtons targetType="personal" targetId={profile.id} ownerUserId={profile.user_id} order={["follow", "fan"]} showCount={false} className="w-full justify-center" />
                  </div>
                  <MessageButton targetUserId={profile.user_id} variant="secondary" label="Message" className="w-full justify-center" />
                  {/* Staff Messaging V1 — renders only for authorized staff. The ordinary
                      Message action above is unchanged for everyone. */}
                  <StaffMessageButton targetUserId={profile.user_id} className="w-full justify-center" />
                </div>
              )}
              {socialLinks.length > 0 && (
                <div className="mt-4 flex flex-wrap items-center gap-1" data-testid="profile-social-links">
                  {socialLinks.map(([k, Icon, label]) => (
                    <a key={k} href={links[k]} target="_blank" rel="noreferrer" title={label} aria-label={label} onClick={() => trackOutboundLink("personal", profile.id, k === "website" ? "website" : `social_${k}`, links[k])}>
                      <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-primary"><Icon className="h-4 w-4" /></Button>
                    </a>
                  ))}
                </div>
              )}
              {showSection(profile.classifications) && (
                <div className="mt-4 flex flex-wrap gap-1.5" data-testid="profile-classifications">
                  {profile.classifications.map((c) => <Badge key={c} className="bg-teal-50 text-teal-700">{labelFor(references.profile_classifications, c)}</Badge>)}
                </div>
              )}
            </Card>

            {showSection(profile.affiliations_resolved) && (
              <Card className="border-0 p-6">
                <h3 className="text-sm font-[640] uppercase tracking-wide text-primary">Affiliated With</h3>
                <div className="mt-3 space-y-3">
                  {profile.affiliations_resolved.map((a) => {
                    const inner = (
                      <>
                        <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg bg-secondary">
                          {a.profile_image_url ? <img src={fileUrl(a.profile_image_url)} alt="" className="h-full w-full object-cover" /> : <span className="text-sm font-[600] text-muted-foreground">{(a.display_name || "?")[0]}</span>}
                        </div>
                        <div><div className="text-sm font-[560] text-foreground">{a.display_name || "—"}</div>{a.role && <div className="text-xs text-muted-foreground">{a.role}</div>}</div>
                      </>
                    );
                    // Issue 12: no-longer-visible affiliation -> gray, non-clickable (no status wording).
                    return (a.visible === false || !a.slug) ? (
                      <div key={a.id} className="flex items-center gap-3 opacity-50" data-testid={`affiliation-view-${a.id}`}>{inner}</div>
                    ) : (
                      <Link key={a.id} to={`/${a.target_type === "capital" ? "capital" : "b"}/${a.slug}`} className="flex items-center gap-3 hover:opacity-80" data-testid={`affiliation-view-${a.id}`}>{inner}</Link>
                    );
                  })}
                </div>
              </Card>
            )}

            {!publicMode && (
              <ConnectionsCard userId={profile.user_id} profileType="personal" profileId={profile.id} targetName={profile.display_name} />
            )}

            {hasContributions && (
              <Card className="border-0 p-6" data-testid="profile-contributions">
                <h3 className="text-sm font-[640] uppercase tracking-wide text-muted-foreground">Contributions</h3>
                <div className={`mt-3 space-y-2.5 ${(events.length + resources.length) > 5 ? "max-h-72 overflow-y-auto pr-1" : ""}`}>
                  {events.map((e) => (
                    <Link key={e.id} to={`/event/${e.slug}`} className="flex items-center gap-3 hover:opacity-80" data-testid={`contribution-event-${e.slug}`} onClick={() => trackInternalLink("personal", profile.id, "event", "event", e.id)}>
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center bg-secondary text-muted-foreground"><CalendarDays className="h-4 w-4" /></div>
                      <div className="min-w-0"><div className="truncate text-sm font-[560] text-foreground">{e.title}</div><div className="text-xs text-muted-foreground">Event</div></div>
                    </Link>
                  ))}
                  {resources.map((r) => (
                    <Link key={r.id} to={`/resource/${r.slug}`} className="flex items-center gap-3 hover:opacity-80" data-testid={`contribution-resource-${r.slug}`} onClick={() => trackInternalLink("personal", profile.id, "resource", "resource", r.id)}>
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center bg-secondary text-muted-foreground"><FileText className="h-4 w-4" /></div>
                      <div className="min-w-0"><div className="truncate text-sm font-[560] text-foreground">{r.title}</div><div className="text-xs text-muted-foreground">Resource</div></div>
                    </Link>
                  ))}
                </div>
              </Card>
            )}

            {!publicMode && <PublicQna targetType="personal" targetId={profile.id} />}
          </div>

          {/* RIGHT (wide) */}
          <div className="space-y-5 lg:col-span-8">
            {publicMode && (
              <Card className="border-0 p-6" data-testid="public-profile-join-cta">
                <h3 className="text-lg font-[640] text-primary">Connect with {firstName} on Startup Otter</h3>
                <p className="mt-2 text-sm leading-7 text-muted-foreground">
                  Messaging, connections and introductions are for members. Create a free account to reach out.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link to="/register"><Button className="rounded-full" data-testid="public-profile-join-button">Join Startup Otter</Button></Link>
                  <Link to={`/login?next=/p/${profile.slug}`}><Button variant="ghost" className="rounded-full" data-testid="public-profile-signin-button">Sign in to connect</Button></Link>
                </div>
              </Card>
            )}

            {!profile.is_owner && !publicMode && (
              <Card className="border-0 p-6">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <InquiryButton type="personal_meeting" targetId={profile.id} targetName={profile.display_name} />
                  <AskQuestionButton targetType="personal" targetId={profile.id} targetName={profile.display_name} />
                </div>
              </Card>
            )}

            {profile.intro_video?.url && (
              <Card className="border-0 p-6"><VideoEmbed video={profile.intro_video} /></Card>
            )}

            {(hasBackground || hasIndustries || hasSkills) && (
              <Card className="border-0 p-6">
                {hasBackground && (
                  <>
                    <h3 className="text-lg font-[640] text-primary">My Story</h3>
                    <div className="mt-3"><RichTextView html={profile.background} className="prose-so-spacious leading-[1.15rem]" /></div>
                  </>
                )}
                {hasIndustries && (
                  <div className={hasBackground ? "mt-6" : ""}>
                    <h3 className="text-lg font-[640] text-primary">Industries</h3>
                    <div className="mt-3 flex flex-wrap gap-1.5">{profile.industries.map((i) => <Badge key={i} variant="secondary" className="bg-secondary">{labelFor(references.industries, i)}</Badge>)}</div>
                  </div>
                )}
                {hasSkills && (
                  <div className={(hasBackground || hasIndustries) ? "mt-6" : ""}>
                    <h3 className="text-lg font-[640] text-primary">Skills &amp; Interests</h3>
                    <div className="mt-3 flex flex-wrap gap-1.5">{profile.skills.map((s) => <Badge key={s} variant="secondary" className="bg-secondary">{s}</Badge>)}</div>
                  </div>
                )}
              </Card>
            )}

            {!publicMode && <ChatterFeed profileType="personal" profileId={profile.id} ownerName={firstName} />}
          </div>
        </div>
      </div>
    </div>
  );
}
